"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.OAuthMessage = exports.Message = exports.appendQueryParams = exports.StatusCode = exports.CACHE_REPLACEMENT_SUPPORTED = exports.REVALIDATION_SUPPORTED = void 0;
var tslib_1 = require("tslib");
var error_1 = require("../error");
/**
 * Checks whether the user uses a browser which does support revalidation.
 */
// only chromium based browsers are supporting cache revalidations with the cache-control: no-cache directive
// @ts-ignore
exports.REVALIDATION_SUPPORTED = typeof navigator === 'undefined' || ((_b = (_a = navigator.userAgentData) === null || _a === void 0 ? void 0 : _a.brands) === null || _b === void 0 ? void 0 : _b.some(function (data) { return data.brand === 'Chromium'; }));
// webkit does not support cache replacement https://stackoverflow.com/questions/32571769/cache-control-no-cache-in-request-header-response-does-not-replace-previously-c
// @ts-ignore
exports.CACHE_REPLACEMENT_SUPPORTED = typeof navigator === 'undefined' || exports.REVALIDATION_SUPPORTED || /firefox/i.test(navigator.userAgent);
exports.StatusCode = {
    NOT_MODIFIED: 304,
    BAD_CREDENTIALS: 460,
    BUCKET_NOT_FOUND: 461,
    INVALID_PERMISSION_MODIFICATION: 462,
    INVALID_TYPE_VALUE: 463,
    FORBIDDEN: 403,
    OBJECT_NOT_FOUND: 404,
    OBJECT_OUT_OF_DATE: 412,
    PERMISSION_DENIED: 466,
    QUERY_DISPOSED: 467,
    QUERY_NOT_SUPPORTED: 468,
    SCHEMA_NOT_COMPATIBLE: 469,
    SCHEMA_STILL_EXISTS: 470,
    SYNTAX_ERROR: 471,
    TRANSACTION_INACTIVE: 472,
    TYPE_ALREADY_EXISTS: 473,
    TYPE_STILL_REFERENCED: 474,
    SCRIPT_ABORTION: 475,
};
/**
 * Appends the given query parameters to the url
 * @param url - on which the parameters will be appended
 * @param queryParams - The Query parameters which should be appended
 * @return The URL with the appended parameters
 */
function appendQueryParams(url, queryParams) {
    var queryString = typeof queryParams === 'string' ? queryParams : Object.entries(queryParams)
        .filter(function (_a) {
        var value = _a[1];
        return value !== undefined;
    })
        .map(function (_a) {
        var key = _a[0], value = _a[1];
        return "".concat(key, "=").concat(encodeURIComponent(value));
    })
        .join('&');
    if (!queryString) {
        return url;
    }
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + queryString;
}
exports.appendQueryParams = appendQueryParams;
var Message = /** @class */ (function () {
    /**
     * @param args The path arguments
     */
    function Message() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        this.withCredentials = false;
        this.progressCallback = null;
        this._tokenStorage = null;
        this._responseType = null;
        var index = 0;
        var path = this.spec.path[0];
        var len = this.spec.path.length;
        for (var i = 1; i < len; i += 1) {
            if (this.spec.dynamic && len - 1 === i) {
                path += args[index].split('/').map(encodeURIComponent).join('/');
            }
            else {
                path += encodeURIComponent(args[index]) + this.spec.path[i];
            }
            index += 1;
        }
        var queryParams = {};
        for (var i = 0; i < this.spec.query.length; i += 1) {
            var arg = args[index];
            index += 1;
            if (arg !== undefined && arg !== null) {
                queryParams[this.spec.query[i]] = arg;
            }
        }
        this.request = {
            method: this.spec.method,
            path: appendQueryParams(path, queryParams),
            entity: null,
            headers: {},
        };
        if (args[index]) {
            this.entity(args[index], 'json');
        }
        this.responseType('json');
    }
    Object.defineProperty(Message.prototype, "spec", {
        /**
         * Returns the specification of this message
         */
        get: function () { return null; },
        enumerable: false,
        configurable: true
    });
    /**
     * Creates a new message class with the given message specification
     * @return A created message object for the specification
     */
    Message.create = function (specification) {
        var parts = specification.path.split('?');
        var path = parts[0].split(/[:*]\w*/);
        var query = [];
        if (parts[1]) {
            parts[1].split('&').forEach(function (arg) {
                var part = arg.split('=');
                query.push(part[0]);
            });
        }
        var spec = {
            path: path,
            query: query,
            status: specification.status,
            method: specification.method,
            dynamic: specification.path.indexOf('*') !== -1,
        };
        return /** @class */ (function (_super) {
            tslib_1.__extends(class_1, _super);
            function class_1() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            Object.defineProperty(class_1.prototype, "spec", {
                get: function () {
                    return spec;
                },
                enumerable: false,
                configurable: true
            });
            return class_1;
        }(Message));
    };
    Object.defineProperty(Message.prototype, "isBinary", {
        get: function () {
            return (this.request.type && this.request.type in Message.BINARY) || this._responseType in Message.BINARY;
        },
        enumerable: false,
        configurable: true
    });
    Message.prototype.tokenStorage = function (value) {
        if (value === undefined) {
            return this._tokenStorage;
        }
        this._tokenStorage = value;
        return this;
    };
    Message.prototype.path = function (path) {
        if (path !== undefined) {
            var queryIndex = this.request.path.indexOf('?') + 1;
            this.request.path = path + (queryIndex > 0 ? (path.indexOf('?') > -1 ? '&' : '?') + this.request.path.substring(queryIndex) : '');
            return this;
        }
        return this.request.path;
    };
    Message.prototype.header = function (name, value) {
        if (value === null) {
            delete this.request.headers[name];
            return this;
        }
        if (value !== undefined) {
            this.request.headers[name] = value;
            return this;
        }
        return this.request.headers[name];
    };
    /**
     * Sets the entity type
     * @param data - The data to send
     * @param type - the type of the data one of 'json'|'text'|'blob'|'arraybuffer'
     * defaults detect the type based on the body data
     * @return This message object
     */
    Message.prototype.entity = function (data, type) {
        var requestType = type;
        if (!requestType) {
            if (typeof data === 'string') {
                if (/^data:(.+?)(;base64)?,.*$/.test(data)) {
                    requestType = 'data-url';
                }
                else {
                    requestType = 'text';
                }
            }
            else if (typeof Blob !== 'undefined' && data instanceof Blob) {
                requestType = 'blob';
            }
            else if (typeof Buffer !== 'undefined' && data instanceof Buffer) {
                requestType = 'buffer';
            }
            else if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
                requestType = 'arraybuffer';
            }
            else if (typeof FormData !== 'undefined' && data instanceof FormData) {
                requestType = 'form';
            }
            else {
                requestType = 'json';
            }
        }
        this.request.type = requestType;
        this.request.entity = data;
        return this;
    };
    Message.prototype.mimeType = function (mimeType) {
        return this.header('content-type', mimeType);
    };
    Message.prototype.contentLength = function (contentLength) {
        if (contentLength !== undefined) {
            return this.header('content-length', "".concat(contentLength));
        }
        return Number(this.header('content-length'));
    };
    Message.prototype.ifMatch = function (eTag) {
        return this.header('If-Match', this.formatETag(eTag));
    };
    Message.prototype.ifNoneMatch = function (eTag) {
        return this.header('If-None-Match', this.formatETag(eTag));
    };
    Message.prototype.ifUnmodifiedSince = function (date) {
        // IE 10 returns UTC strings and not an RFC-1123 GMT date string
        return this.header('if-unmodified-since', date && date.toUTCString().replace('UTC', 'GMT'));
    };
    /**
     * Indicates that the request should not be served by a local cache
     * @return
     */
    Message.prototype.noCache = function () {
        if (!exports.REVALIDATION_SUPPORTED) {
            this.ifMatch('') // is needed for firefox or safari (but forbidden for chrome)
                .ifNoneMatch('-'); // is needed for edge and ie (but forbidden for chrome)
        }
        return this.cacheControl('max-age=0, no-cache');
    };
    Message.prototype.cacheControl = function (value) {
        return this.header('cache-control', value);
    };
    Message.prototype.acl = function (acl) {
        return this.header('baqend-acl', acl && JSON.stringify(acl));
    };
    Message.prototype.customHeaders = function (customHeaders) {
        return this.header('baqend-custom-headers', customHeaders && JSON.stringify(customHeaders));
    };
    Message.prototype.accept = function (accept) {
        return this.header('accept', accept);
    };
    Message.prototype.responseType = function (type) {
        if (type !== undefined) {
            this._responseType = type;
            return this;
        }
        return this._responseType;
    };
    Message.prototype.progress = function (callback) {
        if (callback !== undefined) {
            this.progressCallback = callback;
            return this;
        }
        return this.progressCallback;
    };
    /**
     * Adds the given string to the request path
     *
     * If the parameter is an object, it will be serialized as a query string.
     *
     * @param query which will added to the request path
     * @return
     */
    Message.prototype.addQueryString = function (query) {
        this.request.path = appendQueryParams(this.request.path, query);
        return this;
    };
    Message.prototype.formatETag = function (eTag) {
        if (eTag === null || eTag === undefined || eTag === '*') {
            return eTag;
        }
        var tag = "".concat(eTag);
        if (tag.indexOf('"') === -1) {
            tag = "\"".concat(tag, "\"");
        }
        return tag;
    };
    /**
     * Handle the receive
     * @param response The received response headers and data
     * @return
     */
    Message.prototype.doReceive = function (response) {
        if (this.spec.status.indexOf(response.status) === -1) {
            throw new error_1.CommunicationError(this, response);
        }
    };
    Message.StatusCode = exports.StatusCode;
    Message.BINARY = {
        blob: true,
        buffer: true,
        stream: true,
        arraybuffer: true,
        'data-url': true,
        base64: true,
    };
    return Message;
}());
exports.Message = Message;
var OAuthMessage = /** @class */ (function (_super) {
    tslib_1.__extends(OAuthMessage, _super);
    function OAuthMessage() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(OAuthMessage.prototype, "spec", {
        get: function () {
            return {
                method: 'OAUTH',
                dynamic: false,
                path: [''],
                query: [],
                status: [200],
            };
        },
        enumerable: false,
        configurable: true
    });
    return OAuthMessage;
}(Message));
exports.OAuthMessage = OAuthMessage;
//# sourceMappingURL=data:application/json;base64,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